import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Mic, Send, Bot, Sparkles, TrendingUp, TrendingDown } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fcfa } from "@/lib/orderStatus";

let _msgId = 0;
const nextMsgId = () => ++_msgId;

export default function Copilote() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("voix");
  return (
    <div className="px-4 pt-6 flex flex-col h-[calc(100vh-7rem)]">
      <h1 className="font-display text-2xl font-extrabold tracking-tight mb-1 flex items-center gap-2">
        <Sparkles className="h-6 w-6 text-[#E85D04]" /> {t("copilote.title")}
      </h1>
      <p className="text-sm text-[#5C574F] mb-4">{t("copilote.sub")}</p>
      <div className="flex bg-white rounded-2xl p-1 border border-black/5 mb-4">
        {[["voix", t("copilote.voice")], ["chat", t("copilote.assistant")]].map(([m, l]) => (
          <button key={m} data-testid={`copilote-tab-${m}`} onClick={() => setTab(m)} className={`flex-1 h-11 rounded-xl text-sm font-semibold transition-colors ${tab === m ? "bg-[#E85D04] text-white" : "text-[#5C574F]"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "voix" ? <VoiceEntry /> : <ChatBot />}
    </div>
  );
}

function VoiceEntry() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [listening, setListening] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const recRef = useRef(null);
  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    if (!supported) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "fr-FR";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      const t = Array.from(e.results).map((r) => r[0].transcript).join("");
      setText(t);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
  }, [supported]);

  const toggle = () => {
    if (!supported) { toast.error(t("copilote.notSupported")); return; }
    if (listening) { recRef.current?.stop(); setListening(false); }
    else { setText(""); recRef.current?.start(); setListening(true); }
  };

  const submit = async () => {
    if (!text.trim()) { toast.error(t("copilote.saySomething")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/parse-transaction", { text });
      setHistory([data.transaction, ...history]);
      setText("");
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(t("copilote.txSaved"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 text-center mb-4">
        <button
          data-testid="mic-btn"
          onClick={toggle}
          className={`relative h-24 w-24 mx-auto rounded-full flex items-center justify-center transition-colors ${listening ? "bg-[#1B4332]" : "bg-[#E85D04]"}`}
        >
          {listening && (
            <>
              <span className="absolute inset-0 rounded-full bg-[#1B4332]/40 ripple-ring" />
              <span className="absolute inset-0 rounded-full bg-[#1B4332]/30 ripple-ring" style={{ animationDelay: "0.5s" }} />
            </>
          )}
          <Mic className="h-10 w-10 text-white relative" strokeWidth={2.4} />
        </button>
        <p className="mt-4 text-sm font-medium text-[#5C574F]">{listening ? t("copilote.listening") : t("copilote.pressDictate")}</p>
        <p className="text-xs text-[#9c968a] mt-1">{t("copilote.example")}</p>
      </div>

      <textarea
        data-testid="voice-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("copilote.dictPlaceholder")}
        rows={2}
        className="w-full bg-white rounded-xl border border-[#E5E0D8] p-3 outline-none focus:border-[#E85D04] resize-none mb-3"
      />
      <button data-testid="submit-voice-btn" onClick={submit} disabled={busy} className="w-full h-14 rounded-2xl bg-[#1B4332] text-white font-bold active:scale-95 transition-transform disabled:opacity-60 mb-4">
        {busy ? t("copilote.analyzing") : t("copilote.saveTx")}
      </button>

      <div className="flex-1 overflow-y-auto no-scrollbar space-y-2">
        {history.map((tx) => (
          <motion.div key={tx.transaction_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-xl border border-black/5 p-3 flex items-center gap-3" data-testid={`tx-${tx.transaction_id}`}>
            <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${tx.type === "vente" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#D00000]/10 text-[#D00000]"}`}>
              {tx.type === "vente" ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{tx.description}</p>
              <p className="text-xs text-[#9c968a] capitalize">{tx.type === "vente" ? t("copilote.sale") : t("copilote.expense")}</p>
            </div>
            <span className={`font-display font-bold ${tx.type === "vente" ? "text-[#2D6A4F]" : "text-[#D00000]"}`}>{fcfa(tx.amount)}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function ChatBot() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([{ id: nextMsgId(), role: "ai", text: t("copilote.greeting") }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const msg = input;
    setMessages((m) => [...m, { id: nextMsgId(), role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg });
      setMessages((m) => [...m, { id: nextMsgId(), role: "ai", text: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { id: nextMsgId(), role: "ai", text: t("copilote.unavailable") }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 overflow-y-auto no-scrollbar space-y-3 pb-3">
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "ai" && <span className="h-8 w-8 rounded-lg bg-[#E85D04]/10 flex items-center justify-center mr-2 shrink-0 self-end"><Bot className="h-5 w-5 text-[#E85D04]" /></span>}
              <div data-testid={`msg-${m.role}-${i}`} className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#1B4332] text-white rounded-br-sm" : "bg-white border border-black/5 rounded-bl-sm"}`}>
                {m.text}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {busy && <div className="flex items-center gap-2 text-[#9c968a] text-sm"><span className="h-2 w-2 rounded-full bg-[#E85D04] animate-bounce" /> {t("copilote.thinking")}</div>}
        <div ref={endRef} />
      </div>
      <div className="flex items-center gap-2 pt-2 border-t border-[#E5E0D8]">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={t("copilote.yourMessage")}
          className="flex-1 h-12 bg-white rounded-2xl px-4 outline-none border border-[#E5E0D8] focus:border-[#E85D04]"
        />
        <button data-testid="send-chat-btn" onClick={send} disabled={busy} className="h-12 w-12 rounded-2xl bg-[#E85D04] text-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-60">
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
